import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../config";
import "./ActivateAccount.css";

function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [accountInfo, setAccountInfo] = useState(null);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const accountType = accountInfo?.account_type || "subscriber";
  const isPlatformUser = accountType === "platform";
  const termsVersion = "agent_book_crm_user_agreement_v1.0";

  const hostname = window.location.hostname.toLowerCase();
  const isSubscriberSubdomain = useMemo(() => {
    return (
      hostname !== "agentbookcrm.com" &&
      hostname !== "www.agentbookcrm.com" &&
      hostname !== "app.agentbookcrm.com" &&
      hostname.endsWith(".agentbookcrm.com")
    );
  }, [hostname]);

  useEffect(() => {
    const validateToken = async () => {
      try {
        if (!token) {
          throw new Error("Activation token is missing");
        }

        const res = await fetch(
          `${API_BASE_URL}/api/subscribers/activate-account/validate?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Activation link is invalid");
        }

        setValid(true);
        setAccountInfo(data);
      } catch (err) {
        console.error("Activation validation error:", err.message);
        setError(err.message);
        setValid(false);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const activationTitle =
    isSubscriberSubdomain
      ? "Activate Subscriber Account"
      : "Activate User Account";

  const agreementTitle = "Agent Book CRM User Agreement";

  const checkboxText =
    "I have read and agree to the Agent Book CRM User Agreement.";

  const accountLabel = isPlatformUser ? "Agent Book CRM Account" : "Account";
  const accountName = isPlatformUser
    ? "Agent Book CRM"
    : accountInfo?.subscriber?.business_name || "Subscriber Account";

  const emailLabel = "User Email";

  const introText = isPlatformUser
    ? "Welcome to Agent Book CRM. Review the user agreement and create your password to complete setup."
    : `Welcome${
        accountInfo?.subscriber?.business_name
          ? ` to ${accountInfo.subscriber.business_name}`
          : ""
      }. Review the user agreement and create your password to complete setup.`;

  const redirectAfterActivation = (data) => {
    const subscriberDomain = String(data?.subscriber?.full_domain || "")
      .trim()
      .toLowerCase();

    const currentHost = window.location.hostname.toLowerCase();

    if (
      data?.account_type !== "platform" &&
      subscriberDomain &&
      currentHost !== subscriberDomain
    ) {
      window.location.replace(`https://${subscriberDomain}/dashboard-home`);
      return;
    }

    navigate("/dashboard-home", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!acceptedTerms) {
        throw new Error("You must agree to the Agent Book CRM User Agreement");
      }

      if (!password || password.length < 8) {
        throw new Error("Password must be at least 8 characters");
      }

      if (password !== confirmPassword) {
        throw new Error("Passwords do not match");
      }

      setSubmitting(true);

      const res = await fetch(`${API_BASE_URL}/api/subscribers/activate-account/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token,
          password,
          accepted_terms: true,
          terms_version: termsVersion
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to complete activation");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("user", JSON.stringify(data.user || {}));
      localStorage.removeItem("demoSession");

      redirectAfterActivation(data);
    } catch (err) {
      console.error("Complete activation error:", err.message);
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderAgreementContent = () => {
    return (
      <>
        <p>
          This User Agreement governs individual access to and use of Agent Book CRM.
          By activating this account, you acknowledge that you are being granted access
          to Agent Book CRM as an authorized user.
        </p>
        <p>
          You agree to use Agent Book CRM only for lawful business purposes and only in
          connection with the account, organization, or role for which your access has
          been authorized.
        </p>
        <p>
          You agree to protect the confidentiality of your login credentials and not to
          share your username, password, activation link, authentication codes, or account
          access with any other person.
        </p>
        <p>
          You agree to protect subscriber, client, lead, policy, communication, and
          business data accessed through Agent Book CRM and to use that data only for
          authorized business purposes.
        </p>
        <p>
          You agree not to misuse the platform, attempt unauthorized access, interfere
          with platform operations, bypass security controls, access records outside your
          authorized scope, or use Agent Book CRM in a way that harms the provider, other
          users, subscribers, clients, or the platform.
        </p>
        <p>
          You understand that activity within Agent Book CRM may be logged, monitored,
          and reviewed for security, compliance, support, troubleshooting, fraud
          prevention, and operational purposes.
        </p>
        <p>
          You are responsible for the information you enter, upload, send, store, or
          manage in Agent Book CRM, and you agree to use the platform in compliance with
          applicable privacy, insurance, communications, and business requirements that
          apply to your use of the system.
        </p>
        <p>
          You understand that your access may depend on your role, account status,
          subscription status, plan features, security requirements, and continued
          authorization by the account or platform administrator.
        </p>
        <p>
          By completing activation, you confirm that you have read, understand, and agree
          to this Agent Book CRM User Agreement.
        </p>
      </>
    );
  };

  if (loading) {
    return (
      <div className="activate-page">
        <div className="activate-orb activate-orb-one" />
        <div className="activate-orb activate-orb-two" />

        <div className="activate-card activate-status-card">
          <div className="activate-brand-mark">AB</div>
          <p className="activate-eyebrow">Account Activation</p>
          <h1 className="activate-title">Preparing your account...</h1>
          <p className="activate-subtitle">
            We are validating your secure activation link.
          </p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="activate-page">
        <div className="activate-orb activate-orb-one" />
        <div className="activate-orb activate-orb-two" />

        <div className="activate-card activate-status-card">
          <div className="activate-brand-mark">AB</div>
          <p className="activate-eyebrow">Activation Unavailable</p>
          <h1 className="activate-title">We could not activate this account</h1>
          <p className="activate-subtitle">
            {error || "This activation link is invalid or expired."}
          </p>
          <button
            type="button"
            className="activate-secondary-btn"
            onClick={() => navigate("/login")}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="activate-page">
      <div className="activate-orb activate-orb-one" />
      <div className="activate-orb activate-orb-two" />

      <form className="activate-card activate-form-card" onSubmit={handleSubmit}>
        <div className="activate-header">
          <div className="activate-brand-mark">AB</div>

          <div>
            <p className="activate-eyebrow">Agent Book CRM</p>
            <h1 className="activate-title">{activationTitle}</h1>
            <p className="activate-subtitle">{introText}</p>
          </div>
        </div>

        <div className="activate-account-panel">
          <div>
            <span className="activate-panel-label">{accountLabel}</span>
            <strong>{accountName}</strong>
          </div>

          {accountInfo?.user?.email && (
            <div>
              <span className="activate-panel-label">{emailLabel}</span>
              <strong>{accountInfo.user.email}</strong>
            </div>
          )}
        </div>

        <div className="activate-terms-card">
          <div className="activate-terms-head">
            <h2>{agreementTitle}</h2>
            <span>v1.0</span>
          </div>

          <div className="activate-terms-scroll">
            {renderAgreementContent()}
          </div>
        </div>

        <label className="activate-checkbox-row">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
          <span>{checkboxText}</span>
        </label>

        <div className="activate-password-grid">
          <div className="activate-field">
            <label>New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
            />
          </div>

          <div className="activate-field">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
            />
          </div>
        </div>

        <button type="submit" className="activate-primary-btn" disabled={submitting}>
          {submitting ? "Completing Setup..." : "Complete Setup"}
        </button>

        <p className="activate-footer-note">
          After activation, you will be signed in and redirected to your dashboard.
        </p>
      </form>
    </div>
  );
}

export default ActivateAccount;